/**
 * 🎯 SEO Component - Advanced Search Engine Optimization
 * 
 * A comprehensive SEO component that handles all aspects of search engine
 * optimization for the portfolio application. Provides dynamic meta tags,
 * structured data, Open Graph tags, and Twitter Cards.
 * 
 * Features:
 * - 🔍 **Dynamic Meta Tags** - Title, description, keywords
 * - 📱 **Social Media Cards** - Open Graph and Twitter Cards  
 * - 🌐 **Multilingual Support** - Internationalization ready
 * - 📊 **Structured Data** - Schema.org JSON-LD support
 * - 🎯 **SEO Best Practices** - Canonical URLs, robots meta
 * - ⚡ **Performance Optimized** - Efficient meta tag management
 * 
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from "../../../../hooks"; 

/**
 * 🎯 SEO Component Props Interface
 * 
 * Comprehensive props for controlling all SEO aspects of a page.
 * All props are optional with intelligent defaults provided.
 */
interface SEOProps {
  /** Page title (will be appended with site name) */
  title?: string;
  /** Meta description for search engines (max 160 chars recommended) */
  description?: string;
  /** Array of relevant keywords for the page content */
  keywords?: string[];
  /** Open Graph image URL for social media sharing */
  ogImage?: string;
  /** Open Graph content type (website, article, profile, etc.) */
  ogType?: string;
  /** Canonical URL to prevent duplicate content issues */
  canonicalUrl?: string;
  /** Structured data object for rich snippets (Schema.org) */
  structuredData?: Record<string, any>;
  /** Prevent search engine indexing (default: false) */
  noindex?: boolean;
  /** Page locale for internationalization (e.g., 'en-US', 'fr-FR') */
  locale?: string;
}

/**
 * SEO component that manages all metadata for search engine optimization
 * and social media sharing
 */
export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = [],
  ogImage = '/og-image.webp',
  ogType = 'website',
  canonicalUrl = 'https://jfc-webcraft.fr',
  structuredData,
  noindex = false,
  locale
}) => {
  // ✅ FIX: Use the correct hook
  const { t, language } = useLanguage();
  
  // Determine which locale to use (parameter or active language)
  const activeLocale = locale || (language === 'en' ? 'en_US' : 'fr_FR');
  
  // ✅ Use translations for default metadata
  const defaultTitle = title || t('seo.title');
  const defaultDescription = description || t('seo.description');
  const defaultKeywords = keywords.length > 0 ? keywords : t('seo.keywords').split(',');
  
  // Build absolute URL for OpenGraph image
  const siteUrl = 'https://jfc-webcraft.fr';
  const absoluteImageUrl = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  
  // Default enriched structured data if none provided
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    'mainEntity': {
      '@type': 'Person',
      'name': 'Jean-François Chaussoy',
      'url': 'https://jfc-webcraft.fr',
      'jobTitle': t('seo.jobTitle'),
      'knowsAbout': ['JavaScript', 'React', 'Node.js', 'Python', 'Data Science'],
      'sameAs': [
        'https://github.com/jfchaussoy',
        'https://www.linkedin.com/in/jfchaussoy-profil'
      ],
      'hasOccupation': {
        '@type': 'Occupation',
        'name': t('seo.occupation'),
        'skills': 'React, TypeScript, Node.js, Python, Data Science'
      },
      'workExample': [
        {
          '@type': 'SoftwareApplication',
          'name': 'Welleat SalesSync',
          'applicationCategory': 'BusinessApplication'
        }
      ]
    }
  };
  
  // Use custom structured data or default
  const jsonLD = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Core tags */}
      <html lang={language === 'en' ? 'en' : 'fr'} />
      <title>{defaultTitle}</title>
      <meta name="description" content={defaultDescription} />
      <meta name="keywords" content={defaultKeywords.join(', ')} />
      
      {/* Indexing control */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* OpenGraph for Facebook, LinkedIn, etc. */}
      <meta property="og:title" content={t('seo.og_title') || defaultTitle} />
      <meta property="og:description" content={t('seo.og_description') || defaultDescription} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:alt" content={`Jean-François Chaussoy - ${defaultTitle}`} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={t('seo.og_type') || ogType} />
      <meta property="og:site_name" content="Jean-François Chaussoy | Portfolio" />
      <meta property="og:locale" content={t('seo.og_locale') || activeLocale} />
      
      {/* Twitter Card properties */}
      <meta name="twitter:card" content={t('seo.twitter_card') || 'summary_large_image'} />
      <meta name="twitter:title" content={t('seo.twitter_title') || defaultTitle} />
      <meta name="twitter:description" content={t('seo.twitter_description') || defaultDescription} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      
      {/* Schema.org structured data for Google rich snippets */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLD)}
      </script>
      
      {/* Preloading important resources */}
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Additional metadata */}
      <meta name="author" content={t('seo.author')} />
      <meta name="theme-color" content="#0a1929" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      
  {/* Security metadata - CSP is defined in index.html */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
    </Helmet>
  );
};

export default SEO;
